import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { LoadingOutlined, PaperClipOutlined, RightOutlined } from '@ant-design/icons';
import { Avatar, Spin } from 'antd';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	getUnreadNotesIndexAndCount,
	getEntityById,
} from '../../utils/DataAccessUtils';
import { openInternalWorkspace } from '../../actions/internal_workspace_actions';
import {
	getContractorWO,
	getContractorOrWorkOrderId,
	getInternalNotesChannelId,
	isNoteValid,
} from '../../utils/WorkOrderUtils';
import { getFileLink } from '../../utils/FileAccessUtils';
import moment from 'moment';
import {
	fetchAllInternalNotesForBuyer,
	fetchAllInternalNotesForSupplier,
} from '../../thunks/messages_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';

const getNotePreviewText = (note) => {
	if (note.text) {
		return note.text;
	}
	if (note.photo) {
		return 'Sent a photo';
	}
	if (note.video) {
		return 'Sent a video';
	}
	if (note.audio) {
		return 'Sent a voice note';
	}
	return '';
};

interface InternalNotesPreviewCardProps {
	workOrder: any;
	currentUser: any;
	isClientView: boolean;
	internalMessages: any;
	openWorkspace: (activeTab?: string) => void;
	fetchInternalNotes: (workOrderId: string) => Promise<any>;
}

const buildInternalSpaceUrl = (workOrder: any, location: any, tab?: string): string => {
	const id = nullSafeGet('id', workOrder);
	const path = `/supplier/workOrders/detail/${id}/internalSpace${tab ? `?tab=${tab}` : ''}`;
	return getLinkWIthBackLinkParams(location, 'Back to Work Orders', path, {
		preserve: true,
	});
};

const InternalNotesPreviewCard: FC<InternalNotesPreviewCardProps> = ({
	workOrder,
	currentUser,
	isClientView,
	internalMessages,
	openWorkspace,
	fetchInternalNotes,
}) => {
	const workOrderId = getContractorOrWorkOrderId(workOrder) || nullSafeGet('id', workOrder);
	const contractorWO = getContractorWO(workOrder, isClientView);

	const lastFetchedIdRef = useRef(null);

	useEffect(() => {
		if (workOrderId && workOrderId !== lastFetchedIdRef.current) {
			lastFetchedIdRef.current = workOrderId;
			fetchInternalNotes(workOrderId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workOrderId]);

	// Internal notes from redux

	const allMessages = nullSafeGetOrElse('messages', internalMessages, []);
	const isLoadingInternalSpace = nullSafeGetOrElse('isLoading', internalMessages, false);
	const hasFetchedOnce = !!internalMessages && !isLoadingInternalSpace;
	const notes = useMemo(() => allMessages.filter((note) => isNoteValid(note)), [allMessages]);
	const notesWithText = notes.filter((note) => note && note.text);
	const hasMediaNotesOnly = notes.length > 0 && notesWithText.length === 0;
	const hasNotes = notesWithText.length > 0;
	const latestNote = hasNotes ? notesWithText[notesWithText.length - 1] : null;
	const { firstUnreadMessageIndex, unreadMessageLength } = useMemo(
		() => getUnreadNotesIndexAndCount(notes, currentUser),
		[notes, currentUser]
	);
	const unreadCount = firstUnreadMessageIndex === -1 ? 0 : unreadMessageLength;
	const hasUnread = unreadCount > 0;

	// Internal attachments — for the supplier-owned WO they live on the messages
	// slice (populated by fetchInternalNotes / fetchInternalWorkspace), so they
	// survive a late /work_order/:id response that doesn't return the field. For
	// subcontractor work orders we still read from the contractor WO record.
	const rawAttachments = useMemo(
		() =>
			contractorWO
				? nullSafeGetOrElse('subInternalAttachments', contractorWO, [])
				: nullSafeGetOrElse('internalAttachments', internalMessages, []),
		[contractorWO, internalMessages]
	);
	const attachmentCount = rawAttachments.length;
	const hasAttachments = attachmentCount > 0;
	const recentAttachments = useMemo(
		() => (hasAttachments ? rawAttachments : []),
		[hasAttachments, rawAttachments]
	);
	const history = useHistory();
	const location = useLocation();
	const handleOpenAttachments = useCallback(() => {
		openWorkspace('attachments');
		history.push(buildInternalSpaceUrl(workOrder, location, 'attachments'));
	}, [openWorkspace, history, workOrder, location]);
	const handleOpenNotes = useCallback(() => {
		openWorkspace('notes');
		history.push(buildInternalSpaceUrl(workOrder, location, 'notes'));
	}, [openWorkspace, history, workOrder, location]);
	const handleOpenWorkspace = useCallback(() => {
		openWorkspace();
		history.push(buildInternalSpaceUrl(workOrder, location));
	}, [openWorkspace, history, workOrder, location]);

	const attachmentsShowHeaderRow = !hasFetchedOnce || hasAttachments;
	const internalNotesShowHeaderRow = !hasFetchedOnce || hasNotes || hasMediaNotesOnly;

	return (
		<div className="rowSpacing">
			<div className="overflow-hidden rounded-lg border border-grey-4">
				{/* Teal Header */}
				<div
					role="button"
					tabIndex={0}
					onClick={handleOpenWorkspace}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							handleOpenWorkspace();
						}
					}}
					className="flex !h-[78px] cursor-pointer flex-col gap-1 bg-teal-1 px-4 py-[18px]"
				>
					<div className="h5medium h-6 text-character-primary">INTERNAL WORKSPACE</div>
					<div className="bodyregular h-[22px] text-character-primary">
						Visible only to your <span className="bodymedium">Internal service team.</span>
					</div>
				</div>

				{/* White Body */}
				<div className="bg-white px-4 py-2 pb-4">
					{/* Attachments: title row only when loading or files exist; else plain empty copy */}
					{attachmentsShowHeaderRow ? (
						<div>
							<div
								className="flex h-[22px] cursor-pointer items-center justify-between"
								onClick={handleOpenAttachments}
							>
								<span className="bodymedium text-character-primary">
									Attachments{hasAttachments ? ' (' + attachmentCount + ')' : ''}
								</span>
								<div className="flex items-center">
									<RightOutlined className="text-[14px] !text-character-primary" />
								</div>
							</div>
							{!hasFetchedOnce ? (
								<div className="bodyregular mt-1 flex items-center gap-2 text-character-secondary">
									<Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />} />
									<span>Loading attachments…</span>
								</div>
							) : (
								<div className="mt-2">
									{recentAttachments.map((att) => {
										return (
											<div key={att.fileId} className="flex items-center gap-2 py-1">
												<PaperClipOutlined className="!text-[14px] !text-character-secondary" />
												<a
													href={getFileLink(att.fileId, att.fileName, 'supplier')}
													target="_blank"
													rel="noopener noreferrer"
													className="bodyregular text-teal-5 hover:text-teal-4"
												>
													{att.fileName}
												</a>
											</div>
										);
									})}
								</div>
							)}
						</div>
					) : (
						<div className="bodyregular mt-1 text-character-primary">No attachments</div>
					)}

					{/* Internal notes: title row only when loading or previewable notes; else plain empty copy */}
					<div className="mt-[12px]">
						{internalNotesShowHeaderRow ? (
							<div>
								<div
									className="flex cursor-pointer items-center justify-between"
									onClick={handleOpenNotes}
								>
									<span className="bodymedium text-character-primary">Internal Notes</span>
									<div className="flex items-center">
										{hasUnread && (
											<span className="footnotemonosystem mr-2 flex h-[16px] w-[20px] items-center justify-center rounded-full bg-character-danger px-1.5 text-white">
												{unreadCount}
											</span>
										)}
										<RightOutlined className="text-[14px] !text-character-primary" />
									</div>
								</div>
								{!hasFetchedOnce ? (
									<div className="bodyregular mt-1 flex items-center gap-2 text-character-secondary">
										<Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />} />
										<span>Loading notes…</span>
									</div>
								) : hasNotes && latestNote ? (
									<div className="mt-2">
										<div className="flex items-center gap-1">
											<span className="bodyregular text-character-primary">
												{getNotePreviewText(latestNote)}
											</span>
											{hasUnread && (
												<span className="Notification_badge !h-[7px] !w-[7px] !bg-character-danger" />
											)}
										</div>
										<div className="flex items-center justify-end gap-[2px]">
											<span className="bodyregular text-character-secondary/45">—</span>
											<Avatar
												size={16}
												className="ml-2 flex-shrink-0 !bg-black align-middle !text-[11px] font-medium text-white"
											>
												{(
													nullSafeGetOrElse('nameGiven', latestNote.noteAddedByContact, '').charAt(
														0
													) +
													nullSafeGetOrElse('nameFamily', latestNote.noteAddedByContact, '').charAt(
														0
													)
												).toUpperCase() || '?'}
											</Avatar>
											<span className="footnoteregular ml-0.5 text-character-primary">
												{(
													nullSafeGetOrElse('nameGiven', latestNote.noteAddedByContact, '') +
													' ' +
													nullSafeGetOrElse('nameFamily', latestNote.noteAddedByContact, '')
												).trim() || latestNote.noteAddedBy}
											</span>
											<span className="bodyregular mx-0.5 text-character-primary">∙</span>
											<span className="footnoteregular text-character-secondary">
												{moment(latestNote.noteAddedAt).format('MMM DD h:mma')}
											</span>
										</div>
									</div>
								) : (
									<div className="bodyregular mt-1 text-character-primary">
										Media attachments only
									</div>
								)}
							</div>
						) : (
							<div className="bodyregular text-character-primary">No notes</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	const detailId = nullSafeGet('id', state.work_orders.detail);
	const workOrder = detailId
		? getEntityById(detailId, state.work_orders)
		: state.work_orders.detail;
	const woId = getContractorOrWorkOrderId(workOrder) || nullSafeGet('id', workOrder);
	const channelId = woId ? getInternalNotesChannelId(woId) : null;
	return {
		workOrder: workOrder,
		currentUser: state.session.currentUser,
		isClientView: state.clientView.isClientView,
		internalMessages: channelId ? state.messages[channelId] : null,
	};
};

const mapDispatchToProps = (dispatch) => ({
	openWorkspace: (activeTab) => dispatch(openInternalWorkspace(activeTab)),
	fetchInternalNotesForBuyer: (workOrderId) => dispatch(fetchAllInternalNotesForBuyer(workOrderId)),
	fetchInternalNotesForSupplier: (workOrderId) =>
		dispatch(fetchAllInternalNotesForSupplier(workOrderId)),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
	const isBuyer = nullSafeGet('currentUser.userType', stateProps) === ROLE_TYPES.BUYER;
	const { fetchInternalNotesForBuyer, fetchInternalNotesForSupplier, ...rest } = dispatchProps;
	return {
		...ownProps,
		...stateProps,
		...rest,
		fetchInternalNotes: isBuyer ? fetchInternalNotesForBuyer : fetchInternalNotesForSupplier,
	};
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(InternalNotesPreviewCard);
